/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import { getFields } from 'src/azure/services/work-items';
import { findAllFieldTypesByWorkItemField } from '../../utils/find-all-fields-types-by-wi-field';
import { getField, getFieldType } from 'src/azure/services/get-field';
import { EditWorkItemActionType, EditWorkItemErrorsType, InputValueTypes } from '../types';
import { arrayIsEmpty, stringRequired } from '../../utils/validations';


export const initialEditWorkItem: EditWorkItemActionType = {
	relationship: 'this-work-item',
	workItemField: '',
	inputValueType: 'undefined',
	selectValue: [],
	textValue: '',
	fieldActionValue: '',
	dateTimeValue: {
		type: 'datetime',
		datetime: {
			value: new Date(),
		},
		timeframe: {
			timeNumber: 0,
			timeUnit: 'minutes',
			timeOption: 'ago',
		},
	},
	userFieldValue: [],
	iterationOrAreaPathValue: '',
	copyFieldValue: '',
};

export function useEditWorkItem() {
	const getFieldOptions = useCallback(async (args: {
    projectId: string;
  }) => {
		const fields = await getFields(args.projectId);
		if (fields) {
			const foundFields = fields.filter(field => field.referenceName !== 'System.TeamProject');
			return findAllFieldTypesByWorkItemField(foundFields)
				.map(item => ({ label: item.name, value: item.referenceName }));
		}

		return [];
	}, []);

	const getFieldValueType = useCallback(async (fieldName: string): Promise<InputValueTypes> => {
		const field = await getField(fieldName);
		const fieldTypes: Array<{variants: Array<string>; result: 'text' | 'number' | 'datetime' }> = [
			{
				variants: ['Integer', 'Double'],
				result: 'number',
			},
			{
				variants: ['PlainText', 'String', 'Guid', 'Html'],
				result: 'text'
			},
			{
				variants: ['DateTime'],
				result: 'datetime'
			},
		];

		if (field.isIdentity) {
			return 'user-field';
		}

		if (['System.AreaPath','System.IterationPath'].includes(fieldName)) {
			return 'iteration-or-area-path';
		}

		if (
			[
				field.isPicklist,
				getFieldType(field.type) === 'Boolean',
				fieldName === 'Microsoft.VSTS.Common.Activity',
				fieldName === 'Microsoft.VSTS.Common.Priority',
				fieldName === 'System.WorkItemType',
				fieldName === 'System.State',
				fieldName === 'Microsoft.VSTS.CMMI.Escalate',
				fieldName === 'Microsoft.VSTS.CMMI.RequirementType',
				fieldName === 'Microsoft.VSTS.CMMI.Committed',
				fieldName === 'Microsoft.VSTS.CMMI.UserAcceptanceTest',
				fieldName === 'Microsoft.VSTS.CMMI.MeetingType',
				fieldName === 'Microsoft.VSTS.CMMI.TaskType',
				fieldName === 'Microsoft.VSTS.CMMI.RequiresReview',
				fieldName === 'Microsoft.VSTS.CMMI.RequiresTest',
				fieldName === 'Microsoft.VSTS.CMMI.RootCause',
				fieldName === 'Microsoft.VSTS.CMMI.Blocked',
				fieldName === 'Microsoft.VSTS.Common.Severity',
				fieldName === 'Microsoft.VSTS.Common.ValueArea',
				fieldName === 'Microsoft.VSTS.Common.Risk',
				fieldName === 'Microsoft.VSTS.Common.ResolvedReason',
				fieldName === 'Microsoft.VSTS.Common.Triage',
				fieldName === 'Microsoft.VSTS.Common.Discipline',
				fieldName === 'Microsoft.VSTS.Build.IntegrationBuild',
				fieldName === 'Microsoft.VSTS.Build.FoundIn',
				fieldName === 'System.BoardColumn',
				fieldName === 'System.Reason'
			].some((isTrue) => isTrue)
		) {
			return 'select';
		}

		if (field.referenceName === 'System.Tags') {
			return 'multi-select';
		}

		const foundFieldType = fieldTypes.find((itemField) => itemField.variants.includes(getFieldType(field.type)));
		if (!field.isPicklist && foundFieldType) {
			return foundFieldType.result;
		}
  
		return 'undefined';
	}, []);

	const validate = useCallback((values: EditWorkItemActionType): {
		hasErrors: () => boolean;
		errors: EditWorkItemErrorsType;
	} => {
		let hasErrors = false;
		const validations = {
			workItemField: [
				stringRequired(values.workItemField, 'This field is required'),
			],
			fieldActionValue: [
				stringRequired(values.fieldActionValue, 'This field is required'),
			],
			selectValue: [
				arrayIsEmpty(values.selectValue, 'This field is required'),
			],
			userField: [
				arrayIsEmpty(values.userFieldValue, 'This field is required')
			],
			textValue: [
				stringRequired(values.textValue, 'This field is required'),
			],
			iterationOrAreaPathValue: [
				stringRequired(values.iterationOrAreaPathValue, 'This field is required'),
			],
			copyFieldValue: [
				stringRequired(values.copyFieldValue, 'This field is required'),
			],
		};
	
		const getErrorByInputName = (name: 'workItemField' | 'fieldActionValue' | 'copyFieldValue') => {
			let resultError: string | null = null;
			const validation = new Map<string, Array<string | undefined>>()
				.set('workItemField', validations.workItemField)
				.set('fieldActionValue', validations.fieldActionValue)
				.set('copyFieldValue', validations.copyFieldValue)
				.get(name);

			if (validation) {
				const errors = validation.find(item => item !== undefined);

				if (errors) {
					hasErrors = true;
				}
				resultError = errors || null;
			}

			return resultError;
		};
	
		const getErrorByInputValueType = (inputValueTypeName: InputValueTypes) => {
			let resultError: string | null = null;
			const validation = new Map<string, Array<string | undefined>>()
				.set('select', validations.selectValue)
				.set('multi-select', validations.selectValue)
				.set('number', validations.textValue)
				.set('text', validations.textValue)
				.set('iteration-or-area-path', validations.iterationOrAreaPathValue)
				.set('user-field', validations.userField)
				.get(inputValueTypeName);

			if (validation && inputValueTypeName === values.inputValueType) {
				const errors = validation.find(item => item !== undefined);
				if (errors) {
					hasErrors = true;
				}
				resultError = errors || null;
			}

			return resultError;
		};

		const validationByFieldAction = (fieldAction: string, method: () => string | null) => {
			if (
				fieldAction === 'clear-field' ||
				fieldAction === 'copy-field-value' ||
				fieldAction === 'copy-field-value-from-parent-work-item'
			) {
				return null;
			}

			return method();
		};
		
		return {
			hasErrors: () => hasErrors,
			errors: {
				workItemField: getErrorByInputName('workItemField'),
				fieldActionValue: getErrorByInputName('fieldActionValue'),
				selectValue: validationByFieldAction(values.fieldActionValue, () => getErrorByInputValueType('select')),
				userFieldValue: validationByFieldAction(values.fieldActionValue, () => getErrorByInputValueType('user-field')),
				textValue: validationByFieldAction(
					values.fieldActionValue,
					() => values.inputValueType === 'text' || values.inputValueType === 'number' ? getErrorByInputValueType('text') : null
				),
				iterationOrAreaPathValue: validationByFieldAction(values.fieldActionValue, () => getErrorByInputValueType('iteration-or-area-path')),
				dateTimeValue: validationByFieldAction(values.fieldActionValue, () => getErrorByInputValueType('datetime')),
				copyFieldValue: values.fieldActionValue === 'copy-field-value' ? getErrorByInputName('copyFieldValue') : null,
			}
		};
	}, []);

	const getValues = useCallback((data: EditWorkItemActionType) => {
		const setValueByFieldAction = (value: any) => {
			const isClearField = data.fieldActionValue === 'clear-field';
			const isCopyFieldValue = data.fieldActionValue === 'copy-field-value' || data.fieldActionValue === 'copy-field-value-from-parent-work-item';
			
			if (isClearField) {
				return '';
			}

			if (isCopyFieldValue) {
				return data.copyFieldValue;
			}

			return value;
		};

		if (data.inputValueType === 'select') {
			return {
				field: data.workItemField,
				fieldAction: data.fieldActionValue,
				relationship: data.relationship,
				value: setValueByFieldAction(data.selectValue[0])
			};
		}

		if (data.inputValueType === 'user-field') {
			return {
				field: data.workItemField,
				fieldAction: data.fieldActionValue,
				relationship: data.relationship,
				value: setValueByFieldAction(data.userFieldValue[0]),
			};
		}

		if (data.inputValueType === 'multi-select') {
			return {
				field: data.workItemField,
				fieldAction: data.fieldActionValue,
				relationship: data.relationship,
				value: setValueByFieldAction(data.selectValue)
			};
		}

		if (data.inputValueType === 'datetime') {
			return {
				field: data.workItemField,
				fieldAction: data.fieldActionValue,
				relationship: data.relationship,
				value: setValueByFieldAction(data.dateTimeValue)
			};
		}

		if (data.inputValueType === 'iteration-or-area-path') {
			return {
				field: data.workItemField,
				fieldAction: data.fieldActionValue,
				relationship: data.relationship,
				value: setValueByFieldAction(data.iterationOrAreaPathValue)
			};
		}
		
		return {
			field: data.workItemField,
			fieldAction: data.fieldActionValue,
			relationship: data.relationship,
			value: setValueByFieldAction(data.textValue)
		};
	}, []);

	const loadValues = useCallback(async (values: {
		field: string;
		action: string;
		relationship: string;
		value: any;
	}) => {
		const data: EditWorkItemActionType = {
			relationship: values.relationship,
			workItemField: '',
			inputValueType: 'undefined',
			selectValue: [],
			textValue: '',
			fieldActionValue: '',
			dateTimeValue: {
				type: 'datetime',
				datetime: {
					value: new Date(),
				},
				timeframe: {
					timeNumber: 0,
					timeUnit: 'minutes',
					timeOption: 'ago',
				},
			},
			userFieldValue: [],
			iterationOrAreaPathValue: '',
			copyFieldValue: '',
		};

		const fieldType = await getFieldValueType(values.field);
		data.workItemField = values.field;
		data.fieldActionValue = values.action;
		data.inputValueType = fieldType;

		const isCopyFieldValue = values.action === 'copy-field-value' || values.action === 'copy-field-value-from-parent-work-item';
		const isSetValue = values.action === 'set-value';

		if (isCopyFieldValue) {
			data.copyFieldValue = values.value;
		}

		if (fieldType === 'select' && isSetValue) {
			data.selectValue = [values.value];
		}

		if (fieldType === 'multi-select' && isSetValue) {
			data.selectValue = values.value;
		}
		
		if (fieldType === 'number' || fieldType === 'text') {
			data.textValue = values.value;
		}

		if (fieldType === 'datetime' && isSetValue) {
			if (values.value.type === 'datetime') {
				data.dateTimeValue = {
					type: 'datetime',
					datetime: {
						value: new Date(values.value.datetime.value),
					},
					timeframe: {
						timeNumber: 0,
						timeUnit: 'minutes',
						timeOption: 'ago',
					},
				};
			}

			if (values.value.type === 'timeframe') {
				data.dateTimeValue = {
					type: 'datetime',
					datetime: { 
						value: new Date(),
					},
					timeframe: {
						timeNumber: values.value.timeframe.timeNumber,
						timeUnit: values.value.timeframe.timeUnit,
						timeOption: values.value.timeframe.timeOption,
					},
				};
			}
		}
		
		if (fieldType === 'user-field' && isSetValue) {
			data.userFieldValue = [values.value];
		}

		if (fieldType === 'iteration-or-area-path' && isSetValue) {
			data.iterationOrAreaPathValue = values.value;
		}

		return data;
	}, []);

	return {
		getFieldOptions,
		getFieldValueType,
		validate,
		getValues,
		loadValues
	};
}